// src/app/time-attendance/services/punch-history.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  GetPunchHistoryParams,
  PunchHistoryFilterValues,
  PunchHistoryFilterValuesResponse,
  PunchHistoryLog,
  PunchHistoryResponse,
} from '../models/punch-history.model';

@Injectable({ providedIn: 'root' })
export class PunchHistoryService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getFilterValues(): Observable<PunchHistoryFilterValues> {
    return this.http
      .get<PunchHistoryFilterValuesResponse>(`${this.baseUrl}/api/users/filter-values`)
      .pipe(
        map((res) => ({
          companies: (res.data?.companies ?? []).map((c) => ({
            id: c.id,
            name: c.name,
          })),
          departments: (res.data?.departments ?? []).map((d) => ({
            id: d.id,
            name: d.name,
            code: String(d.code),
          })),
          workerTypes: (res.data?.workerTypes ?? []).map((w) => ({
            id: w.id,
            name: w.name,
          })),
          shifts: (res.data?.shifts ?? []).map((s) => ({
            id: s.id,
            name: s.name,
          })),
          payGroups: (res.data?.payGroups ?? []).map((p) => ({
            id: p.id,
            name: p.name,
          })),
          workOrderNumbers: (res.data?.workOrderNumbers ?? []).map((w) => ({
            id: w.id,
            won: w.won,
          })),
          scheduledDays: (res.data?.scheduledDays ?? []).map((d) => ({
            name: d.name,
            value: d.value,
          })),
          scheduledHours: (res.data?.scheduledHours ?? []).map((h) => ({
            id: h.id,
            name: h.name,
            timeZoneId: h.timeZoneId ?? null,
          })),
        })),
      );
  }

  getPunchHistory(params: GetPunchHistoryParams = {}): Observable<PunchHistoryResponse> {
    let httpParams = new HttpParams()
      .set('pageNumber', String(params.pageNumber ?? 1))
      .set('pageSize', String(params.pageSize ?? 25));

    if (params.searchTerm?.trim()) {
      httpParams = httpParams.set('searchTerm', params.searchTerm.trim());
    }

    if (params.userId != null) {
      httpParams = httpParams.set('userId', String(params.userId));
    }

    if (params.companyId != null) {
      httpParams = httpParams.set('companyId', String(params.companyId));
    }

    if (params.departmentId != null) {
      httpParams = httpParams.set('departmentId', String(params.departmentId));
    }

    if (params.workerTypeId != null) {
      httpParams = httpParams.set('workerTypeId', String(params.workerTypeId));
    }

    if (params.shiftId != null) {
      httpParams = httpParams.set('shiftId', String(params.shiftId));
    }

    if (params.payGroupId != null) {
      httpParams = httpParams.set('payGroupId', String(params.payGroupId));
    }

    if (params.workOrderNumberId != null) {
      httpParams = httpParams.set('workOrderNumberId', String(params.workOrderNumberId));
    }

    if (params.scheduledDay) {
      httpParams = httpParams.set('scheduledDay', params.scheduledDay);
    }

    if (params.scheduledHourId != null) {
      httpParams = httpParams.set('scheduledHourId', String(params.scheduledHourId));
    }

    if (params.logType) {
      httpParams = httpParams.set('logType', params.logType);
    }

    if (params.violation) {
      httpParams = httpParams.set('violation', params.violation);
    }

    if (params.startDate) {
      httpParams = httpParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      httpParams = httpParams.set('endDate', params.endDate);
    }

    if (params.orderBy) {
      httpParams = httpParams.set('orderBy', params.orderBy);
    }

    return this.http
      .get<any>(`${this.baseUrl}/api/user-clock-logs`, { params: httpParams })
      .pipe(
        map((res): PunchHistoryResponse => ({
          status: res.status ?? '',
          totalCount: Number(res.totalCount ?? 0),
          totalPages: Number(res.totalPages ?? 0),
          currentPage: Number(res.currentPage ?? 1),
          pageSize: Number(res.pageSize ?? params.pageSize ?? 25),
          data: (res.data ?? []).map(
            (row: any): PunchHistoryLog => ({
              id: row.id,
              user: row.user
                ? {
                    id: row.user.id,
                    firstName: row.user.firstName ?? '',
                    lastName: row.user.lastName ?? '',
                    employeeNumber: row.user.employeeNumber ?? null,
                    rfid: row.user.rfid ?? null,
                  }
                : null,
              clockDevice: row.clockDevice
                ? {
                    id: row.clockDevice.id,
                    name: row.clockDevice.name ?? '',
                  }
                : null,
              logType: row.logType ?? '',
              violation: row.violation
                ? {
                    type: row.violation.type ?? '',
                  }
                : null,
              schedule: row.schedule
                ? {
                    id: row.schedule.id ?? null,
                    name: row.schedule.name ?? null,
                  }
                : null,
              roundedLogTime: row.roundedLogTime ?? null,
              logTime: row.logTime ?? '',
              updatedAt: row.updatedAt ?? null,
              updatedBy: row.updatedBy
                ? {
                    id: row.updatedBy.id,
                    firstName: row.updatedBy.firstName ?? '',
                    lastName: row.updatedBy.lastName ?? '',
                  }
                : null,
            }),
          ),
        })),
      );
  }
}